import { useCallback } from 'react';
import { buildLessonDefaultsFromProgram } from '@court-sync/shared';
import type { Program } from '@court-sync/shared';

export const PROGRAM_LESSON_TEMPLATE_HELPER =
  'Start from a development program to prefill sport, skill level, age group, description, and cover image. You can still edit any field afterward. Changing the program updates these fields again.';

type ProgramTemplateSetValue = (
  name: 'programId' | 'academySportId' | 'skillLevel' | 'ageGroupId' | 'description' | 'imageUrl',
  value: unknown,
  options?: { shouldDirty?: boolean; shouldValidate?: boolean }
) => void;

export interface UseProgramLessonTemplateOptions {
  programs: Program[];
  setValue: ProgramTemplateSetValue;
  /** Clears pending file preview when inheriting a URL from the program. */
  clearCoverUi?: () => void;
  /** Form empty value for programId — `''` for legacy term forms, `null` for react-hook-form nullable fields. */
  emptyProgramId?: '' | null;
}

export function useProgramLessonTemplate({
  programs,
  setValue,
  clearCoverUi,
  emptyProgramId = null,
}: UseProgramLessonTemplateOptions) {
  const applyProgramTemplate = useCallback(
    (programId: string | null) => {
      const normalizedId = programId && programId.trim().length > 0 ? programId : null;
      setValue('programId', normalizedId ?? emptyProgramId, {
        shouldDirty: true,
        shouldValidate: true,
      });

      if (!normalizedId) {
        return;
      }

      const program = programs.find((p) => p.id === normalizedId);
      if (!program) {
        return;
      }

      const defaults = buildLessonDefaultsFromProgram(program);

      if (defaults.academySportId) {
        setValue('academySportId', defaults.academySportId, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
      setValue('skillLevel', defaults.skillLevel, { shouldDirty: true, shouldValidate: true });
      setValue('ageGroupId', defaults.ageGroupId ?? '', { shouldDirty: true, shouldValidate: true });
      setValue('description', defaults.description ?? '', { shouldDirty: true, shouldValidate: true });
      clearCoverUi?.();
      setValue('imageUrl', defaults.imageUrl ?? '', { shouldDirty: true, shouldValidate: true });
    },
    [programs, setValue, clearCoverUi, emptyProgramId]
  );

  return { applyProgramTemplate, helperText: PROGRAM_LESSON_TEMPLATE_HELPER };
}
